// =====================================================
// DISCOUNTS ADMIN PAGE - Tipos de Descuento
// Gestión de descuentos con asignación a centros
// =====================================================

import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  Tag,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Edit2,
  Power,
  Loader2,
  Save,
  X,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Percent,
  Euro,
  Building2,
  Check,
} from 'lucide-react';
import {
  discountsApi,
  type Discount,
  type DiscountLinkedSite,
  type DiscountType,
  DISCOUNT_TYPE_LABELS,
  formatDiscountValue,
} from '../../api/discounts';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { toast } from '../../components/ui/Toast';

type SortField = 'name' | 'code' | 'value' | 'createdAt';

interface DiscountFormData {
  code: string;
  name: string;
  description: string;
  type: DiscountType;
  value: number;
}

export function DiscountsAdminPage() {
  // Data state
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<any>(null);

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<DiscountType | ''>('');
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Modal state - Crear/Editar
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Discount | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Modal state - Centros
  const [isSitesModalOpen, setIsSitesModalOpen] = useState(false);
  const [sitesItem, setSitesItem] = useState<Discount | null>(null);
  const [linkedSites, setLinkedSites] = useState<DiscountLinkedSite[]>([]);
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [isSavingSites, setIsSavingSites] = useState(false);
  const [showClearSitesConfirm, setShowClearSitesConfirm] = useState(false);
  const [showSelectAllSitesConfirm, setShowSelectAllSitesConfirm] = useState(false);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    item: Discount | null;
    action: 'activate' | 'deactivate';
  }>({ isOpen: false, item: null, action: 'deactivate' });
  const [isProcessing, setIsProcessing] = useState(false);

  // Form
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<DiscountFormData>({
    defaultValues: {
      code: '',
      name: '',
      description: '',
      type: 'PERCENTAGE',
      value: 0,
    },
  });

  const watchType = watch('type');

  // Load data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await discountsApi.getAll({
        page,
        limit,
        search: searchQuery || undefined,
        sortBy,
        sortOrder,
        includeInactive: showInactive,
        type: filterType || undefined,
      });
      setDiscounts(result.data);
      setMeta(result.meta);
    } catch (err: any) {
      setError(err.message || 'Error al cargar descuentos');
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, searchQuery, sortBy, sortOrder, showInactive, filterType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handlers
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormError(null);
    reset({
      code: '',
      name: '',
      description: '',
      type: 'PERCENTAGE',
      value: 0,
    });
    setIsModalOpen(true);
  };

  const handleEdit = (item: Discount) => {
    setEditingItem(item);
    setFormError(null);
    reset({
      code: item.code,
      name: item.name,
      description: item.description || '',
      type: item.type,
      value: item.value,
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!isSaving) {
      setIsModalOpen(false);
      setEditingItem(null);
      setFormError(null);
    }
  };

  const handleSave = async (formData: DiscountFormData) => {
    setFormError(null);
    setIsSaving(true);

    try {
      const payload = {
        code: formData.code,
        name: formData.name,
        description: formData.description || undefined,
        type: formData.type,
        value: formData.value,
      };

      if (editingItem) {
        await discountsApi.update(editingItem.id, payload);
        toast.success('Actualizado', `Descuento "${formData.name}" actualizado`);
      } else {
        await discountsApi.create(payload);
        toast.success('Creado', `Descuento "${formData.name}" creado`);
      }
      setIsModalOpen(false);
      setEditingItem(null);
      loadData();
    } catch (err: any) {
      setFormError(err.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = (item: Discount) => {
    setConfirmDialog({
      isOpen: true,
      item,
      action: item.isActive ? 'deactivate' : 'activate',
    });
  };

  const handleConfirmToggle = async () => {
    if (!confirmDialog.item) return;
    setIsProcessing(true);
    try {
      if (confirmDialog.action === 'deactivate') {
        await discountsApi.deactivate(confirmDialog.item.id);
        toast.success('Desactivado', `Descuento "${confirmDialog.item.name}" desactivado`);
      } else {
        await discountsApi.reactivate(confirmDialog.item.id);
        toast.success('Activado', `Descuento "${confirmDialog.item.name}" activado`);
      }
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo completar la acción');
    } finally {
      setIsProcessing(false);
      setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' });
    }
  };

  // =====================================================
  // MODAL: CENTROS
  // =====================================================
  const handleOpenSitesModal = async (item: Discount) => {
    setSitesItem(item);
    setIsSitesModalOpen(true);
    setIsLoadingSites(true);

    try {
      const data = await discountsApi.getLinkedSites(item.id);
      setLinkedSites(data);

      const linked = data.filter((s) => s.isLinked);
      setSelectedSiteIds(linked.map((s) => s.id));
    } catch (err: any) {
      toast.error('Error', 'No se pudieron cargar los centros');
      setIsSitesModalOpen(false);
    } finally {
      setIsLoadingSites(false);
    }
  };

  const handleCloseSitesModal = () => {
    if (!isSavingSites) {
      setIsSitesModalOpen(false);
      setSitesItem(null);
      setLinkedSites([]);
      setSelectedSiteIds([]);
      setShowClearSitesConfirm(false);
      setShowSelectAllSitesConfirm(false);
    }
  };

  const handleToggleSite = (siteId: string) => {
    setSelectedSiteIds((prev) =>
      prev.includes(siteId) ? prev.filter((id) => id !== siteId) : [...prev, siteId]
    );
  };

  const handleClearSites = () => {
    setSelectedSiteIds([]);
    setShowClearSitesConfirm(false);
  };

  const handleSelectAllSites = () => {
    const allSiteIds = linkedSites.map((site) => site.id);
    setSelectedSiteIds(allSiteIds);
    setShowSelectAllSitesConfirm(false);
  };

  const handleSaveSites = async () => {
    if (!sitesItem) return;
    setIsSavingSites(true);
    try {
      await discountsApi.updateSites(sitesItem.id, {
        siteIds: selectedSiteIds,
      });
      toast.success('Guardado', 'Centros actualizados');
      setIsSitesModalOpen(false);
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo guardar');
    } finally {
      setIsSavingSites(false);
    }
  };

  // Helpers
  const clearAllFilters = () => {
    setShowInactive(false);
    setSearchQuery('');
    setFilterType('');
    setPage(1);
  };

  const hasActiveFilters = showInactive || searchQuery || filterType;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tipos de Descuento</h1>
          <p className="text-sm text-gray-500 mt-1">Gestión de descuentos aplicables en presupuestos</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center flex-1 max-w-2xl gap-2">
            <form onSubmit={handleSearch} className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por código o nombre..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </form>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                showFilters
                  ? 'bg-blue-50 border-blue-200 text-blue-600'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filtros
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              disabled={isLoading}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualizar"
            >
              <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 shadow-md shadow-blue-100 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Nuevo descuento
            </button>
          </div>
        </div>

        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mr-2">
              Filtros activos:
            </span>
            {showInactive && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Incluye inactivos
                <button onClick={() => { setShowInactive(false); setPage(1); }} className="ml-1.5 hover:text-blue-900">
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            {filterType && (
              <div className="inline-flex items-center bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-xs font-medium">
                Tipo: {DISCOUNT_TYPE_LABELS[filterType]}
                <button onClick={() => { setFilterType(''); setPage(1); }} className="ml-1.5 hover:text-purple-900">
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            {searchQuery && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Búsqueda: "{searchQuery}"
                <button onClick={() => { setSearchQuery(''); setPage(1); }} className="ml-1.5 hover:text-blue-900">
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            <button onClick={clearAllFilters} className="text-xs text-gray-400 hover:text-red-500 font-medium ml-2 underline underline-offset-2">
              Limpiar todo
            </button>
          </div>
        )}

        {showFilters && (
          <div className="flex flex-col sm:flex-row sm:items-end justify-between border-t border-gray-100 pt-3 gap-4">
            <div className="flex flex-wrap items-end gap-4">
              <label className="inline-flex items-center cursor-pointer group pb-1">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => { setShowInactive(e.target.checked); setPage(1); }}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-900">Mostrar inactivos</span>
              </label>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Tipo:</span>
                <select
                  value={filterType}
                  onChange={(e) => { setFilterType(e.target.value as DiscountType | ''); setPage(1); }}
                  className="text-sm border-none bg-transparent py-0 pl-0 pr-8 font-medium text-gray-700 focus:ring-0 cursor-pointer hover:text-blue-600"
                >
                  <option value="">Todos</option>
                  <option value="PERCENTAGE">Porcentaje</option>
                  <option value="FIXED">Cantidad fija</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Ordenar:</span>
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field as SortField);
                    setSortOrder(order as 'asc' | 'desc');
                    setPage(1);
                  }}
                  className="text-sm border-none bg-transparent py-0 pl-0 pr-8 font-medium text-gray-700 focus:ring-0 cursor-pointer hover:text-blue-600"
                >
                  <option value="name-asc">Nombre (A-Z)</option>
                  <option value="name-desc">Nombre (Z-A)</option>
                  <option value="code-asc">Código (A-Z)</option>
                  <option value="value-desc">Mayor valor</option>
                  <option value="value-asc">Menor valor</option>
                  <option value="createdAt-desc">Más reciente</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Ver:</span>
                <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                  {[10, 25, 50].map((n) => (
                    <button
                      key={n}
                      onClick={() => { setLimit(n); setPage(1); }}
                      className={`px-3 py-1 text-xs font-semibold border-r border-gray-200 last:border-r-0 transition-colors ${
                        limit === n ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {discounts.length === 0 && !isLoading ? (
          <div className="p-12 text-center">
            <Tag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {hasActiveFilters ? 'Sin resultados' : 'No hay descuentos'}
            </h3>
            <p className="text-gray-500">
              {hasActiveFilters ? 'No se encontraron descuentos con ese criterio' : 'Crea tu primer tipo de descuento'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Código</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Nombre</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Tipo</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Valor</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Centros</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {discounts.map((item) => (
                    <tr key={item.id} className={`hover:bg-gray-50 ${!item.isActive ? 'bg-gray-50/50' : ''}`}>
                      <td className="px-6 py-4">
                        <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {item.code}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <span className={`font-medium ${item.isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                            {item.name}
                          </span>
                          {item.description && (
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{item.description}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                          item.type === 'PERCENTAGE'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {item.type === 'PERCENTAGE' ? (
                            <Percent className="h-3 w-3" />
                          ) : (
                            <Euro className="h-3 w-3" />
                          )}
                          {DISCOUNT_TYPE_LABELS[item.type]}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="font-semibold text-gray-900">
                          {formatDiscountValue(item.type, item.value)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {item.sitesCount > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 text-xs font-medium rounded-full">
                            <Building2 className="h-3 w-3" />
                            {item.sitesCount}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Sin asignar</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => handleToggleStatus(item)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                            item.isActive ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                          role="switch"
                          aria-checked={item.isActive}
                        >
                          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                            item.isActive ? 'translate-x-5' : 'translate-x-0'
                          }`}>
                            <Power className={`h-3 w-3 absolute top-1 left-1 ${item.isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                          </span>
                        </button>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleOpenSitesModal(item)}
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                            title="Asignar centros"
                          >
                            <Building2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                            title="Editar"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {isLoading && (
              <div className="flex items-center justify-center py-8 border-t">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              </div>
            )}

            {meta && meta.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Mostrando {(meta.page - 1) * meta.limit + 1} a {Math.min(meta.page * meta.limit, meta.total)} de {meta.total}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(meta.page - 1)}
                    disabled={!meta.hasPrevPage || isLoading}
                    className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700">Página {meta.page} de {meta.totalPages}</span>
                  <button
                    onClick={() => setPage(meta.page + 1)}
                    disabled={!meta.hasNextPage || isLoading}
                    className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* MODAL: CREAR/EDITAR DESCUENTO */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingItem ? 'Editar descuento' : 'Nuevo descuento'}
        subtitle={editingItem ? `Editando: ${editingItem.name}` : 'Configura el tipo de descuento'}
      >
        <form onSubmit={handleSubmit(handleSave)}>
          {formError && (
            <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-800">Error al guardar</p>
                <p className="text-sm text-red-600 mt-0.5">{formError}</p>
              </div>
              <button type="button" onClick={() => setFormError(null)} className="text-red-400 hover:text-red-600 p-1">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="space-y-5">
            {/* Header visual del tipo de descuento */}
            <div className={`p-4 rounded-2xl border-2 transition-colors ${
              watchType === 'PERCENTAGE' 
                ? 'bg-blue-50 border-blue-200' 
                : 'bg-green-50 border-green-200'
            }`}>
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                  watchType === 'PERCENTAGE' 
                    ? 'bg-blue-100' 
                    : 'bg-green-100'
                }`}>
                  {watchType === 'PERCENTAGE' ? (
                    <Percent className={`h-7 w-7 text-blue-600`} />
                  ) : (
                    <Euro className={`h-7 w-7 text-green-600`} />
                  )}
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-semibold ${
                    watchType === 'PERCENTAGE' ? 'text-blue-900' : 'text-green-900'
                  }`}>
                    {watchType === 'PERCENTAGE' ? 'Descuento porcentual' : 'Descuento de cantidad fija'}
                  </p>
                  <p className={`text-xs mt-0.5 ${
                    watchType === 'PERCENTAGE' ? 'text-blue-600' : 'text-green-600'
                  }`}>
                    {watchType === 'PERCENTAGE' 
                      ? 'Se aplicará un porcentaje sobre el total' 
                      : 'Se descontará una cantidad fija en euros'}
                  </p>
                </div>
              </div>
            </div>

            {/* Código y Nombre */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Código <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('code', { required: 'El código es obligatorio' })}
                  className={`w-full px-4 py-3 border-2 rounded-xl text-sm font-medium uppercase tracking-wide transition-colors focus:ring-0 ${
                    errors.code 
                      ? 'border-red-300 focus:border-red-500 bg-red-50' 
                      : 'border-gray-200 focus:border-blue-500 bg-gray-50 focus:bg-white'
                  }`}
                  placeholder="DESC01"
                />
                {errors.code && <p className="mt-1.5 text-xs text-red-500 font-medium">{errors.code.message}</p>}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Nombre del descuento <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('name', { required: 'El nombre es obligatorio' })}
                  className={`w-full px-4 py-3 border-2 rounded-xl text-sm transition-colors focus:ring-0 ${
                    errors.name 
                      ? 'border-red-300 focus:border-red-500 bg-red-50' 
                      : 'border-gray-200 focus:border-blue-500 bg-gray-50 focus:bg-white'
                  }`}
                  placeholder="Ej: Descuento por pronto pago"
                />
                {errors.name && <p className="mt-1.5 text-xs text-red-500 font-medium">{errors.name.message}</p>}
              </div>
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Descripción <span className="text-gray-400 font-normal">(opcional)</span>
              </label>
              <textarea
                {...register('description')}
                rows={2}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm resize-none bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-0 transition-colors"
                placeholder="Añade una descripción para identificar mejor este descuento..."
              />
            </div>

            {/* Tipo y Valor */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Tipo de descuento <span className="text-red-500">*</span>
                </label>
                <div className="flex rounded-xl border-2 border-gray-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setValue('type', 'PERCENTAGE')}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-all whitespace-nowrap ${
                      watchType === 'PERCENTAGE'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Percent className="h-4 w-4" />
                    Porcentaje
                  </button>
                  <button
                    type="button"
                    onClick={() => setValue('type', 'FIXED')}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-all border-l-2 whitespace-nowrap ${
                      watchType === 'FIXED'
                        ? 'bg-green-500 text-white border-green-500'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border-gray-200'
                    }`}
                  >
                    <Euro className="h-4 w-4" />
                    Fijo (€)
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Valor del descuento <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    {...register('value', {
                      required: 'El valor es obligatorio',
                      setValueAs: (v) => v === '' ? undefined : parseFloat(v),
                      validate: {
                        positive: (v) => v > 0 || 'El valor debe ser positivo',
                        maxPercent: (v) => watchType !== 'PERCENTAGE' || v <= 100 || 'El porcentaje no puede ser mayor a 100',
                      },
                    })}
                    className={`w-full pl-4 pr-12 py-[10px] border-2 rounded-xl text-base font-semibold transition-colors focus:ring-0 focus:outline-none ${
                      errors.value 
                        ? 'border-red-300 focus:border-red-500 bg-red-50' 
                        : watchType === 'PERCENTAGE'
                          ? 'border-gray-200 focus:border-blue-500 bg-gray-50 focus:bg-white'
                          : 'border-gray-200 focus:border-green-500 bg-gray-50 focus:bg-white'
                    }`}
                    placeholder={watchType === 'PERCENTAGE' ? '10' : '50.00'}
                  />
                  <span className={`absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-7 h-7 rounded-lg pointer-events-none ${
                    watchType === 'PERCENTAGE' 
                      ? 'bg-blue-100 text-blue-600' 
                      : 'bg-green-100 text-green-600'
                  }`}>
                    {watchType === 'PERCENTAGE' ? (
                      <Percent className="h-3.5 w-3.5" />
                    ) : (
                      <Euro className="h-3.5 w-3.5" />
                    )}
                  </span>
                </div>
                {errors.value && <p className="mt-1.5 text-xs text-red-500 font-medium">{errors.value.message}</p>}
              </div>
            </div>
          </div>

          <ModalFooter>
            <button
              type="button"
              onClick={handleCloseModal}
              disabled={isSaving}
              className="px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className={`px-6 py-2.5 text-sm font-semibold text-white rounded-xl disabled:opacity-50 flex items-center gap-2 transition-colors shadow-lg ${
                watchType === 'PERCENTAGE'
                  ? 'bg-blue-600 hover:bg-blue-700 shadow-blue-200'
                  : 'bg-green-600 hover:bg-green-700 shadow-green-200'
              }`}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {editingItem ? 'Guardar cambios' : 'Crear descuento'}
                </>
              )}
            </button>
          </ModalFooter>
        </form>
      </Modal>

      {/* MODAL: CENTROS */}
      <Modal
        isOpen={isSitesModalOpen}
        onClose={handleCloseSitesModal}
        title="Asignar centros"
        subtitle={sitesItem ? `${sitesItem.name} (${formatDiscountValue(sitesItem.type, sitesItem.value)})` : ''}
      >
        {isLoadingSites ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl border border-green-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
                  <Building2 className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-green-900">Centros disponibles</p>
                  <p className="text-xs text-green-600">Selecciona los centros donde estará disponible</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowSelectAllSitesConfirm(true)}
                  className="text-xs text-green-600 hover:text-green-800 px-2 py-1 hover:bg-white rounded transition-colors"
                >
                  Seleccionar todo
                </button>
                {selectedSiteIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowClearSitesConfirm(true)}
                    className="text-xs text-green-600 hover:text-red-500 px-2 py-1 hover:bg-white rounded transition-colors"
                  >
                    Limpiar todo
                  </button>
                )}
              </div>
            </div>

            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="max-h-[300px] overflow-y-auto divide-y divide-gray-100">
                {linkedSites.map((site) => {
                  const isSelected = selectedSiteIds.includes(site.id);

                  return (
                    <label
                      key={site.id}
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                        isSelected ? 'bg-green-50/50' : 'bg-white'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSite(site.id)}
                        className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                      <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                        {site.code}
                      </span>
                      <span className={`flex-1 text-sm ${isSelected ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                        {site.name}
                      </span>
                      {isSelected && (
                        <Check className="h-4 w-4 text-green-600" />
                      )}
                    </label>
                  );
                })}
              </div>
            </div>

            {selectedSiteIds.length > 0 && (
              <p className="text-xs text-gray-500 px-1">
                <span className="font-medium text-gray-700">{selectedSiteIds.length}</span> centro{selectedSiteIds.length !== 1 ? 's' : ''} seleccionado{selectedSiteIds.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        )}

        <ModalFooter>
          <button
            type="button"
            onClick={handleCloseSitesModal}
            disabled={isSavingSites}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            Cancelar
          </button>
          <button
            onClick={handleSaveSites}
            disabled={isSavingSites || isLoadingSites}
            className="px-4 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-xl disabled:opacity-50 flex items-center gap-2"
          >
            {isSavingSites ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Guardar
              </>
            )}
          </button>
        </ModalFooter>
      </Modal>

      {/* Confirmación para limpiar centros */}
      <ConfirmDialog
        isOpen={showClearSitesConfirm}
        onClose={() => setShowClearSitesConfirm(false)}
        onConfirm={handleClearSites}
        variant="warning"
        title="¿Limpiar selección?"
        message={`Se quitarán los ${selectedSiteIds.length} centro${selectedSiteIds.length !== 1 ? 's' : ''} seleccionado${selectedSiteIds.length !== 1 ? 's' : ''}.`}
        confirmText="Limpiar"
      />

      {/* Confirmación para seleccionar todo */}
      <ConfirmDialog
        isOpen={showSelectAllSitesConfirm}
        onClose={() => setShowSelectAllSitesConfirm(false)}
        onConfirm={handleSelectAllSites}
        variant="info"
        title="¿Seleccionar todo?"
        message={`Se seleccionarán ${linkedSites.length} centro${linkedSites.length !== 1 ? 's' : ''}.`}
        confirmText="Seleccionar todo"
      />

      {/* Confirm Dialog para activar/desactivar */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => !isProcessing && setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' })}
        onConfirm={handleConfirmToggle}
        isLoading={isProcessing}
        variant={confirmDialog.action === 'deactivate' ? 'warning' : 'info'}
        title={confirmDialog.action === 'deactivate' ? '¿Desactivar descuento?' : '¿Activar descuento?'}
        message={
          confirmDialog.action === 'deactivate'
            ? `El descuento "${confirmDialog.item?.name}" será desactivado y no estará disponible en presupuestos.`
            : `El descuento "${confirmDialog.item?.name}" será reactivado.`
        }
        confirmText={confirmDialog.action === 'deactivate' ? 'Desactivar' : 'Activar'}
      />
    </div>
  );
}

export default DiscountsAdminPage;
